/**
 * Builder-Tab → AgentBus bridge.
 *
 * Today's surface that already produces typed violations against the live
 * project graph: the Rules Builder validator (`lib/ruleValidate.ts`). When
 * it returns errors/warnings, this module translates each into a typed
 * AgentProposal the bus can publish.
 *
 * "No stubs" (feedback_no_stubs.md): this lands inside the existing
 * /rules?tab=builder flow, not a parallel demo route.
 */
import codegen from "../../../../docs/ontology/codegen.json";
import type { AgentProposal, TypedMutation } from "./types";
import type { ValidationIssue, ValidationResult } from "../ruleValidate";

interface FieldSpec { path: string; kind: string; field: string; type: "number" | "string"; unit?: string }
const FIELDS: ReadonlyArray<FieldSpec> = (codegen.fields ?? []) as ReadonlyArray<FieldSpec>;

/** Map a validator finding into a typed AgentProposal. */
export function proposalFromIssue(
  issue: ValidationIssue,
  ctx: { readonly ruleId: string; readonly nowIso: string },
): AgentProposal | null {
  const agentId = "builder-agent";

  // Unknown field — propose the closest typed field path from the ontology.
  if (issue.message.startsWith("Unknown field") && issue.path) {
    const suggested = nearestField(issue.path);
    if (!suggested) return null;
    const proposed: TypedMutation = {
      kind: "update",
      nodeKind: "Rule",
      subjectId: ctx.ruleId,
      attrs: { swapField: { from: issue.path, to: suggested.path } },
      ts: ctx.nowIso,
      origin: "agent",
    };
    return {
      agentId,
      confidence: 0.90, // tight match — same prefix, known field
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/unknown-field"],
      proposed,
    };
  }

  if (issue.message.startsWith("Tautology")) {
    return {
      agentId,
      confidence: 0.88,
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/tautology"],
      proposed: {
        kind: "update",
        nodeKind: "Rule",
        subjectId: ctx.ruleId,
        attrs: { suggestion: "swap-rhs-or-op" },
        ts: ctx.nowIso,
        origin: "agent",
      },
    };
  }

  if (issue.message.startsWith("Unsatisfiable")) {
    return {
      agentId,
      confidence: 0.92,
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/unsat"],
      proposed: {
        kind: "update",
        nodeKind: "Rule",
        subjectId: ctx.ruleId,
        attrs: { suggestion: "swap-op-to-equal-or-different-rhs" },
        ts: ctx.nowIso,
        origin: "agent",
      },
    };
  }

  if (issue.message.startsWith("Range must have lo < hi")) {
    return {
      agentId,
      confidence: 0.95,
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/range-inverted"],
      proposed: {
        kind: "update",
        nodeKind: "Rule",
        subjectId: ctx.ruleId,
        attrs: { suggestion: "swap-lo-hi" },
        ts: ctx.nowIso,
        origin: "agent",
      },
    };
  }

  if (issue.message.startsWith("Set is empty")) {
    return {
      agentId,
      confidence: 0.75,
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/empty-set"],
      proposed: {
        kind: "update",
        nodeKind: "Rule",
        subjectId: ctx.ruleId,
        attrs: { suggestion: "add-default-set-values" },
        ts: ctx.nowIso,
        origin: "agent",
      },
    };
  }

  if (issue.message.startsWith("Type mismatch")) {
    return {
      agentId,
      confidence: 0.80,
      about: { kind: "Rule", id: ctx.ruleId },
      wasInformedBy: ["validator/type-mismatch"],
      proposed: {
        kind: "update",
        nodeKind: "Rule",
        subjectId: ctx.ruleId,
        attrs: { suggestion: "coerce-or-swap-rhs" },
        ts: ctx.nowIso,
        origin: "agent",
      },
    };
  }

  return null;
}

/** Translate a whole ValidationResult into proposals. */
export function proposalsFromValidation(
  result: ValidationResult,
  ctx: { readonly ruleId: string; readonly nowIso: string },
): ReadonlyArray<AgentProposal> {
  const out: AgentProposal[] = [];
  for (const issue of [...result.errors, ...result.warnings]) {
    const p = proposalFromIssue(issue, ctx);
    if (p) out.push(p);
  }
  return out;
}

/** Find the closest typed field path by simple prefix + edit distance.
 *  No fuzz library — Levenshtein over the suffix is enough for typo-level
 *  matches (Room.area vs Room.areaSqM, SKU.price vs SKU.priceMinor). */
function nearestField(input: string): FieldSpec | null {
  const dotIx = input.indexOf(".");
  if (dotIx < 0) return null;
  const cls = input.slice(0, dotIx);
  const suf = input.slice(dotIx + 1);
  const candidates = FIELDS.filter((f) => f.kind === cls);
  if (candidates.length === 0) return null;
  let best: FieldSpec | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    const score = editDistance(c.field, suf);
    if (score < bestScore) {
      best = c;
      bestScore = score;
    }
  }
  // Only suggest if the match is plausibly close (≤ ½ the input length).
  if (best && bestScore <= Math.max(2, Math.floor(suf.length / 2))) return best;
  return null;
}

function editDistance(a: string, b: string): number {
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const dp: number[] = new Array(bl + 1);
  for (let j = 0; j <= bl; j += 1) dp[j] = j;
  for (let i = 1; i <= al; i += 1) {
    let prev = dp[0]!;
    dp[0] = i;
    for (let j = 1; j <= bl; j += 1) {
      const tmp = dp[j]!;
      dp[j] = a[i - 1] === b[j - 1]
        ? prev
        : 1 + Math.min(prev, dp[j]!, dp[j - 1]!);
      prev = tmp;
    }
  }
  return dp[bl]!;
}
